// @ts-nocheck - Temporary fix for backend integration types
import { httpClient } from '@/lib/httpClient';
import { API_CONFIG } from '@/config/api';

export class BackendSearchService {
  private getAuthToken(): string | undefined {
    return localStorage.getItem('auth_token') || undefined;
  }

  // Search providers
  async searchProviders(filters: {
    query?: string;
    location?: string;
    service?: string;
    radius?: number;
    minRating?: number;
    maxPrice?: number;
    availability?: 'now' | 'today' | 'week';
    verified?: boolean;
    emergency?: boolean;
    page?: number;
    limit?: number;
    sortBy?: 'relevance' | 'rating' | 'price' | 'distance';
    sortOrder?: 'asc' | 'desc';
  }): Promise<any> {
    const params: Record<string, string> = {};
    
    if (filters.query) params.q = filters.query;
    if (filters.location) params.location = filters.location;
    if (filters.service) params.service = filters.service;
    if (filters.radius) params.radius = filters.radius.toString();
    if (filters.minRating) params.min_rating = filters.minRating.toString();
    if (filters.maxPrice) params.max_price = filters.maxPrice.toString();
    if (filters.availability) params.availability = filters.availability;
    if (filters.verified !== undefined) params.verified = filters.verified.toString();
    if (filters.emergency !== undefined) params.emergency = filters.emergency.toString();
    if (filters.page) params.page = filters.page.toString();
    if (filters.limit) params.limit = filters.limit.toString();
    if (filters.sortBy) params.sort_by = filters.sortBy;
    if (filters.sortOrder) params.sort_order = filters.sortOrder;

    const response = await httpClient.get(API_CONFIG.ENDPOINTS.SEARCH, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to search providers');
    }

    return response.data;
  }

  // Get search suggestions
  async getSearchSuggestions(query: string, type?: 'service' | 'location' | 'provider'): Promise<string[]> {
    const params: Record<string, string> = { q: query };
    if (type) params.type = type;

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH_SUGGESTIONS}`, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch search suggestions');
    }

    return response.data || [];
  }

  // Get popular searches
  async getPopularSearches(limit = 10): Promise<string[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH}/popular`, {
      params: { limit: limit.toString() },
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch popular searches');
    }

    return response.data || [];
  }

  // Get featured providers
  async getFeaturedProviders(location?: string, service?: string, limit = 6): Promise<any[]> {
    const params: Record<string, string> = { limit: limit.toString() };
    if (location) params.location = location;
    if (service) params.service = service;

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH}/featured`, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch featured providers');
    }

    return response.data || [];
  }

  // Track provider view (for analytics)
  async trackProviderView(providerId: string, searchQuery?: string, position?: number): Promise<void> {
    const response = await httpClient.post(`/analytics/view`, {
      provider_id: providerId,
      search_query: searchQuery,
      position: position,
      timestamp: new Date().toISOString()
    }, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      console.warn('Failed to track provider view:', response.error);
      // Don't throw error for analytics tracking
    }
  }

  // Sync provider index (admin function)
  async syncProviderIndex(providerId: string): Promise<void> {
    const response = await httpClient.post(`/api/v1/sync/provider`, {
      provider_id: providerId
    }, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to sync provider index');
    }
  }

  // Advanced search with multiple filters
  async advancedSearch(filters: {
    // Basic filters
    query?: string;
    location?: string;
    services?: string[];
    
    // Geographic filters
    latitude?: number;
    longitude?: number;
    radius?: number; // in kilometers
    
    // Quality filters
    minRating?: number;
    minReviews?: number;
    verified?: boolean;
    backgroundCheck?: boolean;
    
    // Availability filters
    availableNow?: boolean;
    emergencyService?: boolean;
    weekendService?: boolean;
    
    // Pricing filters
    minPrice?: number;
    maxPrice?: number;
    priceType?: 'hourly' | 'fixed' | 'callout';
    
    // Experience filters
    minExperience?: number;
    certifications?: string[];
    
    // Business filters
    providerType?: 'individual' | 'company';
    teamSize?: 'solo' | 'small' | 'medium' | 'large';
    
    // Pagination and sorting
    page?: number;
    limit?: number;
    sortBy?: 'relevance' | 'rating' | 'price' | 'distance' | 'reviews' | 'experience';
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    providers: any[];
    total: number;
    page: number;
    totalPages: number;
    filters: any;
    suggestions?: string[];
  }> {
    const params: Record<string, string> = {};
    
    // Convert all filter parameters to strings
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params[key] = value.join(',');
        } else {
          params[key] = value.toString();
        }
      }
    });

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH}/advanced`, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to perform advanced search');
    }

    return response.data;
  }

  // Get search filters/facets
  async getSearchFilters(location?: string): Promise<{
    services: Array<{ name: string; count: number }>;
    locations: Array<{ name: string; count: number }>;
    priceRanges: Array<{ min: number; max: number; count: number }>;
    ratings: Array<{ rating: number; count: number }>;
  }> {
    const params: Record<string, string> = {};
    if (location) params.location = location;

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH}/filters`, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch search filters');
    }

    return response.data;
  }

  // Save search (for user search history)
  async saveSearch(searchData: {
    query: string;
    location?: string;
    service?: string;
    filters?: any;
  }): Promise<void> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.SEARCH}/save`, searchData, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      console.warn('Failed to save search:', response.error);
      // Don't throw error for search saving
    }
  }

  // Get user search history
  async getSearchHistory(limit = 10): Promise<any[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH}/history`, {
      params: { limit: limit.toString() },
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch search history');
    }

    return response.data || [];
  }

  // Clear search history
  async clearSearchHistory(): Promise<void> {
    const response = await httpClient.delete(`${API_CONFIG.ENDPOINTS.SEARCH}/history`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to clear search history');
    }
  }

  // Get trending searches
  async getTrendingSearches(location?: string, timeframe = '7d'): Promise<string[]> {
    const params: Record<string, string> = { timeframe };
    if (location) params.location = location;

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH}/trending`, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch trending searches');
    }

    return response.data || [];
  }

  // Get search analytics (for providers)
  async getSearchAnalytics(providerId: string, period = '30d'): Promise<{
    totalViews: number;
    totalClicks: number;
    averagePosition: number;
    topQueries: Array<{ query: string; views: number; clicks: number }>;
    trends: Array<{ date: string; views: number; clicks: number }>;
  }> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.SEARCH}/analytics/${providerId}`, {
      params: { period },
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch search analytics');
    }

    return response.data;
  }
}

export const backendSearchService = new BackendSearchService();